import React, {useMemo, useState} from "react";
import styled from "styled-components";
import OrderDetailComponent from "./userCatalogueDetailsComponent";
import MaterialSelect from "../../common/components/MaterialSelect";

const lucideIcons = require("lucide-react/dist/cjs/lucide-react.cjs");

const SvgIcon = ({children, size = 18, color = 'currentColor'}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {children}
    </svg>
);

const SearchFallbackIcon = ({size, color}) => (
    <SvgIcon size={size} color={color}>
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.3-4.3"/>
    </SvgIcon>
);

const ChevronLeftFallbackIcon = ({size}) => (
    <SvgIcon size={size}>
        <path d="m15 18-6-6 6-6"/>
    </SvgIcon>
);

const ChevronRightFallbackIcon = ({size}) => (
    <SvgIcon size={size}>
        <path d="m9 18 6-6-6-6"/>
    </SvgIcon>
);

const getLucideIcon = (name, fallback) => lucideIcons[name] || lucideIcons.default?.[name] || fallback;
const Search = getLucideIcon('Search', SearchFallbackIcon);
const ChevronLeft = getLucideIcon('ChevronLeft', ChevronLeftFallbackIcon);
const ChevronRight = getLucideIcon('ChevronRight', ChevronRightFallbackIcon);

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;
const ComponentHeader = styled.div`
    display: flex;
    flex-direction: row;
    width: 70%;
    height: max-content;
`;
const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    font-size: 22px;
    align-items: center;
`;
const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    margin-top: 24px;
    padding: 16px;
    border: 1px solid #e4e7ec;
    border-radius: 8px 8px 0 0;
    background: #ffffff;
`;
const FilterGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
`;
const SearchBox = styled.div`
    width: 340px;
    max-width: 100%;
    min-height: 42px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px;
    border: 1px solid #d6d6d6;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;
const SearchInput = styled.input`
    width: 100%;
    border: 0;
    color: #1f2933;
    font-size: 14px;
    font-weight: 500;
    outline: none;
    
    &::placeholder {
        color: #98a2b3;
    }
`;
const ResultMeta = styled.div`
    color: #667085;
    font-size: 14px;
    font-weight: 500;
`;
const TableCard = styled.div`
    overflow: hidden;
    border: 1px solid #e4e7ec;
    border-top: 0;
    border-radius: 0 0 8px 8px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
`;
const CatalogueTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
`;
const TableHead = styled.thead`
    background: #f7f8fa;
`;
const HeadColumn = styled.th`
    padding: 13px 18px;
    border-bottom: 1px solid #e4e7ec;
    color: #667085;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    text-align: left;
    text-transform: uppercase;
`;
const BodyRow = styled.tr`
    cursor: pointer;
    transition: background 0.16s ease, box-shadow 0.16s ease;

    &:hover {
        background: #fbf7f7;
    }
`;
const BodyColumn = styled.td`
    padding: 16px 18px;
    border-bottom: 1px solid #eef0f3;
    color: #344054;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
    vertical-align: middle;
`;
const PrimaryText = styled.div`
    color: #101828;
    font-size: 14px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
const SecondaryText = styled.div`
    margin-top: 3px;
    color: #667085;
    font-size: 12px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 4px 10px;
    border: 1px solid ${props => props.color.border};
    border-radius: 999px;
    color: ${props => props.color.text};
    background: ${props => props.color.bg};
    font-size: 12px;
    font-weight: 700;
`;
const Pagination = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-top: 1px solid #e4e7ec;
`;
const PaginationPages = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`;
const PaginationButton = styled.button`
    min-width: ${props => props.isNav ? '82px' : '36px'};
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 10px;
    border: 1px solid ${props => props.isActive ? '#832729' : '#d0d5dd'};
    border-radius: 6px;
    color: ${props => props.isActive ? '#ffffff' : '#344054'};
    background: ${props => props.isActive ? '#832729' : '#ffffff'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-size: 14px;
    font-weight: 700;
    opacity: ${props => props.disabled ? 0.5 : 1};
    box-shadow: ${props => props.isActive ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.06)'};

    &:hover {
        border-color: ${props => props.disabled || props.isActive ? undefined : '#832729'};
    }
`;
const Ellipsis = styled.span`
    min-width: 28px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #667085;
    font-size: 14px;
    font-weight: 700;
`;
const EmptyState = styled.div`
    padding: 42px 18px;
    color: #667085;
    font-size: 16px;
    text-align: center;
`;
export const ThumbnailImg = styled.img`
    width: 42px;
    height: 42px;
    object-fit: contain;
`;

export const catalogueStatuses = [
    'DRAFT',
    'IN_PROGRESS',
    'PUBLISHED',
    'REJECTED',
];

const statusOptions = [
    {label: 'All statuses', value: 'ALL'},
    ...catalogueStatuses.map((status) => ({label: status, value: status})),
];

const statusColors = {
    DRAFT: {bg: '#f9fafb', border: '#d0d5dd', text: '#475467'},
    IN_PROGRESS: {bg: '#fffaeb', border: '#fedf89', text: '#b54708'},
    PUBLISHED: {bg: '#ecfdf3', border: '#abefc6', text: '#027a48'},
    REJECTED: {bg: '#fef3f2', border: '#fecdca', text: '#b42318'},
};

const getCatalogueStatus = (catalogue) => {
    if (catalogue?.status === 'PUBLISHED' || catalogue?.isARSupported) {
        return 'PUBLISHED';
    }
    if (catalogue?.status === 'DRAFT') {
        return 'DRAFT';
    }
    if (catalogue?.status === 'REJECTED' || catalogue?.status?.includes('REJECTED')) {
        return 'REJECTED';
    }
    if (catalogue?.status) {
        return 'IN_PROGRESS';
    }
    return 'DRAFT';
};

const getCatalogueStatusLabel = (catalogue) => {
    const status = getCatalogueStatus(catalogue);
    if (status === 'IN_PROGRESS') {
        return 'IN_PROGRESS';
    }
    return status;
};

const getPaginationItems = (currentPage, totalPages) => {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    if (currentPage <= 4) {
        return [1, 2, 3, 4, '...', totalPages];
    }

    if (currentPage >= totalPages - 3) {
        return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
};

const CatalogueList = (props) => {
    const orderList = props?.orderList || [];
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const filteredOrders = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        return orderList.filter((orderObj) => {
            const status = getCatalogueStatus(orderObj);
            const matchesStatus = statusFilter === 'ALL' || status === statusFilter;
            const searchableText = [
                orderObj?.category?.name,
                orderObj?.name,
                orderObj?.addedBy?.name,
                status,
            ].filter(Boolean).join(' ').toLowerCase();
            return matchesStatus && (!query || searchableText.includes(query));
        });
    }, [orderList, searchText, statusFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

    const onSearchChange = (event) => {
        setSearchText(event.target.value);
        setCurrentPage(1);
    };

    return (
        <>
            <Toolbar>
                <FilterGroup>
                    <SearchBox>
                        <Search size={18} color="#667085"/>
                        <SearchInput
                            type="text"
                            placeholder="Search catalogue"
                            value={searchText}
                            onChange={onSearchChange}
                        />
                    </SearchBox>
                    <MaterialSelect
                        value={statusFilter}
                        options={statusOptions}
                        width="190px"
                        onChange={(value) => {
                            setStatusFilter(value);
                            setCurrentPage(1);
                        }}
                    />
                </FilterGroup>
                <ResultMeta>
                    Showing {filteredOrders.length ? startIndex + 1 : 0}-{Math.min(startIndex + pageSize, filteredOrders.length)} of {filteredOrders.length}
                </ResultMeta>
            </Toolbar>
            <TableCard>
                {paginatedOrders.length ? <CatalogueTable cellpadding="0" cellspacing="0">
                    <TableHead>
                        <tr>
                            <HeadColumn style={{width: '28%'}}>Product</HeadColumn>
                            <HeadColumn style={{width: '22%'}}>Category</HeadColumn>
                            <HeadColumn style={{width: '20%'}}>Added by</HeadColumn>
                            <HeadColumn style={{width: '16%'}}>Status</HeadColumn>
                            <HeadColumn style={{width: '14%'}}>Updated</HeadColumn>
                        </tr>
                    </TableHead>
                    <tbody>
                        {paginatedOrders.map((orderObj) => {
                            const status = getCatalogueStatus(orderObj);
                            return (
                                <BodyRow
                                    key={orderObj._id}
                                    onClick={() =>
                                        props.onOrderClicked(orderObj)
                                    }
                                >
                                    <BodyColumn>
                                        <PrimaryText>{orderObj?.name || '-'}</PrimaryText>
                                        <SecondaryText>{orderObj?._id}</SecondaryText>
                                    </BodyColumn>
                                    <BodyColumn>{orderObj?.category?.name || '-'}</BodyColumn>
                                    <BodyColumn>{orderObj?.addedBy?.name || '-'}</BodyColumn>
                                    <BodyColumn>
                                        <StatusPill color={statusColors[status]}>
                                            {getCatalogueStatusLabel(orderObj)}
                                        </StatusPill>
                                    </BodyColumn>
                                    <BodyColumn>
                                        {orderObj?.modifiedOn ? new Date(orderObj.modifiedOn).toDateString() : '-'}
                                    </BodyColumn>
                                </BodyRow>
                            );
                        })}
                    </tbody>
                </CatalogueTable> : <EmptyState>No catalogues found.</EmptyState>}
                <Pagination>
                    <PaginationButton
                        isNav
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                    >
                        <ChevronLeft size={16}/>
                        Prev
                    </PaginationButton>
                    <PaginationPages>
                        {getPaginationItems(safePage, totalPages).map((page, index) => (
                            page === '...' ?
                                <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis> :
                                <PaginationButton
                                    key={page}
                                    isActive={page === safePage}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </PaginationButton>
                        ))}
                    </PaginationPages>
                    <PaginationButton
                        isNav
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                    >
                        Next
                        <ChevronRight size={16}/>
                    </PaginationButton>
                </Pagination>
            </TableCard>
        </>
    )
}

function UserCatalogueComponent(props) {
    const {state} = props;
    return (
        <MainContainer>
            <ComponentHeader>
                <SearchContainer>
                    {state.selectedOrder ? 'Catalogue Details' : 'User Catalogues'}
                </SearchContainer>
            </ComponentHeader>
            {state.selectedOrder &&
                <OrderDetailComponent orderDetails={state?.selectedOrder} refreshOrderList={props.refreshOrderList}/>}
            {!state.selectedOrder &&
                <CatalogueList
                    orderList={state?.orderList} onOrderClicked={props.onOrderClicked}/>}
        </MainContainer>
    );
}

export default UserCatalogueComponent;
